/**
 * SlideMetadataEditor
 *
 * Edit-mode form for a slide's AI metadata (title, summary, keywords,
 * category). Used inside SlidePreviewModal when the caller is an admin.
 * The parent owns the draft state and save / cancel flow.
 */

import type React from "react"

import { useTranslation } from "react-i18next"

import type { SlideCategory } from "@/features/slide-sense/types"

import { Plus, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

const CATEGORY_OPTIONS: { value: SlideCategory; labelKey: string }[] = [
  { value: "title", labelKey: "slideSense.categoryTitle" },
  { value: "content", labelKey: "slideSense.categoryContent" },
  { value: "chart", labelKey: "slideSense.categoryChart" },
  { value: "table", labelKey: "slideSense.categoryTable" },
  { value: "image", labelKey: "slideSense.categoryImage" },
  { value: "divider", labelKey: "slideSense.categoryDivider" },
  { value: "closing", labelKey: "slideSense.categoryClosing" },
  { value: "other", labelKey: "slideSense.categoryOther" },
]

export interface SlideMetadataDraft {
  ai_title: string
  ai_summary: string
  ai_keywords: string[]
  category: SlideCategory
}

export interface SlideMetadataEditorProps {
  draft: SlideMetadataDraft
  setDraft: React.Dispatch<React.SetStateAction<SlideMetadataDraft | null>>
  newKeyword: string
  setNewKeyword: (value: string) => void
  onAddKeyword: () => void
  onRemoveKeyword: (kw: string) => void
  sourceFilename: string
}

export function SlideMetadataEditor({
  draft,
  setDraft,
  newKeyword,
  setNewKeyword,
  onAddKeyword,
  onRemoveKeyword,
  sourceFilename,
}: SlideMetadataEditorProps) {
  const { t } = useTranslation()

  return (
    <>
      <div>
        <h4 className="text-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
          {t("slideSense.titleLabel")}
        </h4>
        <Input
          maxLength={300}
          value={draft.ai_title}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, ai_title: e.target.value } : prev))
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-44">
          <Select
            value={draft.category}
            onValueChange={(val) =>
              setDraft((prev) => (prev ? { ...prev, category: val as SlideCategory } : prev))
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-muted-foreground text-xs">{sourceFilename}</span>
      </div>

      <div>
        <h4 className="text-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
          {t("slideSense.summary")}
        </h4>
        <Textarea
          maxLength={2000}
          rows={4}
          value={draft.ai_summary}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, ai_summary: e.target.value } : prev))
          }
        />
      </div>

      <div>
        <h4 className="text-foreground mb-1.5 text-xs font-semibold tracking-wide uppercase">
          {t("slideSense.keywords")}
        </h4>
        <div className="flex flex-wrap items-center gap-1.5">
          {draft.ai_keywords.map((kw) => (
            <Badge key={kw} className="gap-1 pr-1 text-[11px]" variant="secondary">
              {kw}
              <button
                aria-label={t("slideSense.removeKeyword", { name: kw })}
                className="hover:bg-destructive/20 hover:text-destructive -m-1 flex size-6 items-center justify-center rounded-sm"
                type="button"
                onClick={() => onRemoveKeyword(kw)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <div className="flex items-center gap-1">
            <Input
              className="h-7 w-32 text-xs"
              maxLength={60}
              placeholder={t("slideSense.addKeyword")}
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  onAddKeyword()
                }
              }}
            />
            <Button
              className="h-7 w-7"
              disabled={!newKeyword.trim() || draft.ai_keywords.length >= 30}
              size="icon"
              variant="ghost"
              onClick={onAddKeyword}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
